import { useEffect, useRef, useState } from 'react';
import { clients as clientsApi, plans as plansApi } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { apiFetch, getCampaignId } from '../api/client.js';
import {
  STATUS_COLORS, buildDriverColorMap, buildStopMap,
  buildPopupHtml, drawRoutes,
} from './mapHelpers.js';

/** @returns {'driver'|'coordinator'|'admin'|null} Campaign role for current user */
function useCampaignRole() {
  const { user, campaigns, activeCampaign } = useAuth();
  if (user?.globalRole === 'superadmin') return 'admin';
  const entry = campaigns.find((c) => c.campaignId === activeCampaign);
  return entry?.role || null;
}

export default function MapView() {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const layersRef = useRef([]);
  const clusterRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [plansDetailed, setPlansDetailed] = useState([]);
  const [driverPositions, setDriverPositions] = useState([]);
  const [error, setError] = useState('');
  const [showRoutes, setShowRoutes] = useState(true);
  const [myPosition, setMyPosition] = useState(null);
  const myPosMarkerRef = useRef(null);
  const myPosCircleRef = useRef(null);
  const role = useCampaignRole();
  const gpsIntervalRef = useRef(null);
  const myGpsRef = useRef(null);

  useEffect(() => {
    loadData();
    return () => { if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current); };
  }, []);

  // Poll driver positions for coordinators/admins
  useEffect(() => {
    if (role === 'coordinator' || role === 'admin') {
      fetchDriverPositions();
      gpsIntervalRef.current = setInterval(fetchDriverPositions, 15000);
      return () => clearInterval(gpsIntervalRef.current);
    }
  }, [role]);

  /** @returns {Promise<void>} */
  const fetchDriverPositions = async () => {
    try {
      const cid = getCampaignId();
      const positions = await apiFetch(`/c/${cid}/gps`);
      setDriverPositions(positions);
    } catch (_err) {
      // GPS positions not critical
    }
  };

  const loadData = async () => {
    try {
      const [cls, planSummaries] = await Promise.all([
        clientsApi.list(),
        plansApi.list().catch(() => []),
      ]);

      const activePlans = planSummaries.filter((p) => p.status !== 'completed');
      const details = await Promise.all(
        activePlans.slice(0, 20).map((p) => plansApi.get(p.id).catch(() => null))
      );
      const detailedPlans = details.filter(Boolean);
      setPlansDetailed(detailedPlans);

      // Filter clients for driver role
      if (role === 'driver') {
        const clientIds = new Set();
        for (const plan of detailedPlans) {
          for (const stop of plan.stops || []) clientIds.add(stop.client_id);
        }
        setClients(cls.filter((c) => clientIds.has(c.id)));
      } else {
        setClients(cls);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (!mapRef.current || typeof window.L === 'undefined') return;
    renderMap();
  }, [clients, plansDetailed, showRoutes, driverPositions]);

  const renderMap = () => {
    const L = window.L;
    if (!mapInstance.current) {
      mapInstance.current = L.map(mapRef.current).setView([42.15, 9.1], 9);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; CARTO &copy; OSM', maxZoom: 20, subdomains: 'abcd',
      }).addTo(mapInstance.current);
    }

    layersRef.current.forEach((l) => l.remove());
    layersRef.current = [];
    if (clusterRef.current) {
      mapInstance.current.removeLayer(clusterRef.current);
      clusterRef.current = null;
    }

    // Depot marker — La Ferme Corse, Lucciana
    const depotIcon = L.divIcon({
      className: 'depot-icon',
      html: '<div style="width:36px;height:36px;background:linear-gradient(135deg,#1e40af,#0f172a);border:3px solid #fff;border-radius:10px;display:flex;align-items:center;justify-content:center;box-shadow:0 3px 8px rgba(0,0,0,0.35);font-size:18px;">📦</div>',
      iconSize: [36, 36], iconAnchor: [18, 18],
    });
    const depotMarker = L.marker([42.54957, 9.471405], { icon: depotIcon, zIndexOffset: 900 });
    depotMarker.bindPopup(
      '<div style="font-family:system-ui,sans-serif;min-width:180px;">' +
      '<b style="font-size:13px;">Dépôt — La Ferme Corse</b><br/>' +
      '<span style="font-size:11px;color:#64748b;">Route de l\'aéroport, Lucciana</span><hr style="border:none;border-top:1px solid #e2e8f0;margin:6px 0;">' +
      '<div style="display:flex;gap:4px;">' +
      '<a href="https://www.google.com/maps/dir/?api=1&destination=42.54957,9.471405" target="_blank" rel="noopener" style="font-size:11px;background:#059669;color:#fff;padding:4px 10px;border-radius:8px;text-decoration:none;font-weight:500;">Maps</a>' +
      '<a href="https://waze.com/ul?ll=42.54957,9.471405&navigate=yes" target="_blank" rel="noopener" style="font-size:11px;background:#0891b2;color:#fff;padding:4px 10px;border-radius:8px;text-decoration:none;font-weight:500;">Waze</a>' +
      '</div></div>'
    );
    depotMarker.addTo(mapInstance.current);
    layersRef.current.push(depotMarker);

    const driverColorMap = buildDriverColorMap(clients);
    const stopMap = buildStopMap(plansDetailed);
    const clusterGroup = L.markerClusterGroup({ maxClusterRadius: 50 });
    clusterRef.current = clusterGroup;

    const validClients = clients.filter((c) => c.lat && c.lng);
    validClients.forEach((client) => {
      const driverColor = client.assigned_driver ? driverColorMap[client.assigned_driver] : null;
      const color = driverColor || STATUS_COLORS[client.delivery_status] || '#9CA3AF';
      const marker = L.circleMarker([client.lat, client.lng], {
        radius: 8, fillColor: color, color: '#fff', weight: 2, fillOpacity: 0.9,
      });
      const stopInfo = stopMap[client.id];
      const estTime = stopInfo?.estimated_arrival
        ? new Date(stopInfo.estimated_arrival).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        : '';
      marker.bindPopup(buildPopupHtml(client, estTime, stopInfo));
      clusterGroup.addLayer(marker);
    });

    mapInstance.current.addLayer(clusterGroup);

    // Driver position markers (coordinators/admins only)
    for (const pos of driverPositions) {
      const icon = L.divIcon({
        className: 'driver-pos-icon',
        html: '<div style="background:#1e40af;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.3);">L</div>',
        iconSize: [28, 28], iconAnchor: [14, 14],
      });
      const m = L.marker([pos.lat, pos.lng], { icon });
      m.bindPopup(`<b>${pos.driver_name || 'Livreur'}</b><br/>MAJ: ${new Date(pos.updated_at).toLocaleTimeString('fr-FR')}`);
      m.addTo(mapInstance.current);
      layersRef.current.push(m);
    }

    if (showRoutes) drawRoutes(plansDetailed, driverColorMap, mapInstance.current, layersRef);

    // My GPS position (pulsing blue dot)
    updateMyPositionMarker(L);

    if (validClients.length > 0) {
      const bounds = L.latLngBounds(validClients.map((c) => [c.lat, c.lng]));
      mapInstance.current.fitBounds(bounds, { padding: [30, 30] });
    }
  };

  const updateMyPositionMarker = (L) => {
    if (!myPosition || !mapInstance.current) return;
    const { lat, lng, accuracy } = myPosition;

    // Remove old markers
    if (myPosMarkerRef.current) { myPosMarkerRef.current.remove(); }
    if (myPosCircleRef.current) { myPosCircleRef.current.remove(); }

    // Accuracy circle
    myPosCircleRef.current = L.circle([lat, lng], {
      radius: Math.min(accuracy || 50, 500), color: '#3B82F6', fillColor: '#3B82F6',
      fillOpacity: 0.1, weight: 1, opacity: 0.3,
    }).addTo(mapInstance.current);

    // Blue pulsing dot
    const icon = L.divIcon({
      className: 'my-pos-icon',
      html: '<div style="width:16px;height:16px;background:#3B82F6;border:3px solid #fff;border-radius:50%;box-shadow:0 0 8px rgba(59,130,246,0.6);animation:pulse-soft 2s ease-in-out infinite;"></div>',
      iconSize: [16, 16], iconAnchor: [8, 8],
    });
    myPosMarkerRef.current = L.marker([lat, lng], { icon, zIndexOffset: 1000 })
      .bindPopup('<b>Ma position</b>')
      .addTo(mapInstance.current);
  };

  const startMyGps = () => {
    if (!navigator.geolocation) return;
    if (myGpsRef.current) navigator.geolocation.clearWatch(myGpsRef.current);
    myGpsRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setMyPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
  };

  const centerOnMe = () => {
    if (myPosition && mapInstance.current) {
      mapInstance.current.setView([myPosition.lat, myPosition.lng], 15);
    } else {
      startMyGps();
    }
  };

  // Start GPS tracking on mount
  useEffect(() => {
    startMyGps();
    return () => { if (myGpsRef.current) navigator.geolocation.clearWatch(myGpsRef.current); };
  }, []);

  // Update marker when position changes
  useEffect(() => {
    if (myPosition && mapInstance.current && window.L) {
      updateMyPositionMarker(window.L);
    }
  }, [myPosition]);

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-bold">Carte</h2>
        <div className="flex items-center gap-3">
          <button onClick={centerOnMe}
            className="flex items-center gap-1 text-xs font-medium bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">
            <span className="w-2 h-2 bg-white rounded-full inline-block" /> Ma position
          </button>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showRoutes} onChange={(e) => setShowRoutes(e.target.checked)} />
            Itinéraires
          </label>
        </div>
      </div>
      <div className="flex gap-4 mb-3 text-xs flex-wrap">
        <span className="flex items-center gap-1"><span className="w-4 h-4 rounded-lg bg-[#1e40af] inline-block text-center text-[8px] leading-4">📦</span> Dépôt</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gray-400 inline-block" /> En attente</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-400 inline-block" /> En cours</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Livré</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Problème</span>
        {(role === 'coordinator' || role === 'admin') && (
          <span className="flex items-center gap-1"><span className="w-4 h-4 rounded-full bg-blue-800 inline-block text-white text-center text-[8px] leading-4">L</span> Livreur</span>
        )}
        {myPosition && (
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block border-2 border-white shadow" /> Moi</span>
        )}
      </div>
      {error && <p className="text-red-600 mb-2">{error}</p>}
      <div ref={mapRef} className="w-full h-[calc(100vh-20rem)] md:h-[calc(100vh-14rem)] rounded-xl border" />
    </div>
  );
}
